import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Heart, Loader2, Users, Check } from "lucide-react";
import axios from "axios";
import { apiClient } from "../lib/apiClient";


export default function InviteSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("No invitation token provided");
      setLoading(false);
      return;
    }
    // We don't have a GET endpoint for invitations, so just show the form
    // The token will be validated on submission
    setInvitation({ token });
    setLoading(false);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post("authRegisterInvite", {
        token,
        name: form.name,
        password: form.password
      });
      const { access_token, user, group_name } = res.data;
      sessionStorage.setItem("auth_token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setSuccess(group_name);
      setTimeout(() => navigate("/dashboard"), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account. The invitation may be expired.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-stone" data-testid="invite-signup-card">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-dark">Join Prayer Tracker</h1>
            <p className="text-sm text-stone-dark/60 mt-1 flex items-center justify-center gap-1">
              <Users className="w-4 h-4" />
              You've been invited to a prayer group
            </p>
          </div>

          {success ? (
            <div className="text-center py-6" data-testid="invite-success">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-serif text-xl font-semibold text-stone-dark mb-2">Welcome!</h2>
              <p className="text-stone-dark/60">
                You've joined <strong>{success}</strong>. Redirecting to your dashboard...
              </p>
            </div>
          ) : error && !invitation ? (
            <div className="text-center py-6" data-testid="invite-error">
              <p className="text-red-600 mb-4">{error}</p>
              <Link to="/signup">
                <Button className="bg-sage hover:bg-sage-dark text-white rounded-full">
                  Sign Up Instead
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-form">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="invite-form-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                  className="bg-stone-light"
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 6 characters"
                  minLength={6}
                  required
                  className="bg-stone-light"
                  data-testid="invite-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="Re-enter your password"
                  minLength={6}
                  required
                  className="bg-stone-light"
                  data-testid="invite-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-sage hover:bg-sage-dark text-white rounded-full h-11"
                disabled={submitting}
                data-testid="invite-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account & Join Group"}
              </Button>
              <p className="text-center text-xs text-stone-dark/50">
                Already have an account?{" "}
                <Link to="/login" className="text-sage hover:underline">Log in</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
